import { GameplayTier } from '#src/__generated__/globalTypes.ts';

import { clamp } from './math.ts';

export const WHOLE_STEP_GAMEPLAY_TIERS = [
  GameplayTier.GOAT,
  GameplayTier.STAR,
  GameplayTier.IMPACT,
  GameplayTier.ROSTER,
  GameplayTier.DNP,
];

export const GAMEPLAY_TIER_DISPLAY_ORDER: GameplayTier[] = [
  GameplayTier.GOAT,
  GameplayTier.STAR_PLUS,
  GameplayTier.STAR,
  GameplayTier.IMPACT_PLUS,
  GameplayTier.IMPACT,
  GameplayTier.ROSTER_PLUS,
  GameplayTier.ROSTER,
  GameplayTier.DNP_PLUS,
  GameplayTier.DNP,
];

export const isGameplayTier = (value: string): value is GameplayTier => {
  return GAMEPLAY_TIER_DISPLAY_ORDER.includes(value as GameplayTier);
};

const sortByGameplayTier = (a: string, b: string) => {
  if (!isGameplayTier(a)) {
    return 1;
  }
  if (!isGameplayTier(b)) {
    return -1;
  }
  return (
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(a) -
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(b)
  );
};

export { sortByGameplayTier };

// Half-steps round down: only GameplayTierBadge renders them exactly.
export const gameplayTierStarCount = (gameplayTier: GameplayTier) =>
  ({
    [GameplayTier.GOAT]: 5,
    [GameplayTier.STAR_PLUS]: 4,
    [GameplayTier.STAR]: 4,
    [GameplayTier.IMPACT_PLUS]: 3,
    [GameplayTier.IMPACT]: 3,
    [GameplayTier.ROSTER_PLUS]: 2,
    [GameplayTier.ROSTER]: 2,
    [GameplayTier.DNP_PLUS]: 1,
    [GameplayTier.DNP]: 1,
  })[gameplayTier];

export const gameplayTierStarValue = (gameplayTier: GameplayTier) =>
  ({
    [GameplayTier.GOAT]: 5,
    [GameplayTier.STAR_PLUS]: 4.5,
    [GameplayTier.STAR]: 4,
    [GameplayTier.IMPACT_PLUS]: 3.5,
    [GameplayTier.IMPACT]: 3,
    [GameplayTier.ROSTER_PLUS]: 2.5,
    [GameplayTier.ROSTER]: 2,
    [GameplayTier.DNP_PLUS]: 1.5,
    [GameplayTier.DNP]: 1,
  })[gameplayTier];

export const GAMEPLAY_TIER_LABELS: Record<GameplayTier, string> = {
  [GameplayTier.GOAT]: 'Icon',
  [GameplayTier.STAR_PLUS]: 'Star+',
  [GameplayTier.STAR]: 'Star',
  [GameplayTier.IMPACT_PLUS]: 'Key+',
  [GameplayTier.IMPACT]: 'Key',
  [GameplayTier.ROSTER_PLUS]: 'Roster+',
  [GameplayTier.ROSTER]: 'Roster',
  [GameplayTier.DNP_PLUS]: 'Reserve+',
  [GameplayTier.DNP]: 'Reserve',
};

export const NBA_GAMEPLAY_TIER_MIN_L10_SCORES: Record<GameplayTier, number> = {
  [GameplayTier.GOAT]: 50,
  [GameplayTier.STAR_PLUS]: 45,
  [GameplayTier.STAR]: 40,
  [GameplayTier.IMPACT_PLUS]: 35,
  [GameplayTier.IMPACT]: 30,
  [GameplayTier.ROSTER_PLUS]: 25,
  [GameplayTier.ROSTER]: 20,
  [GameplayTier.DNP_PLUS]: 15,
  [GameplayTier.DNP]: 0,
};

const tierRanksAway = (gameplayTier: GameplayTier, ranksUp: number) =>
  GAMEPLAY_TIER_DISPLAY_ORDER[
    GAMEPLAY_TIER_DISPLAY_ORDER.indexOf(gameplayTier) - ranksUp
  ] as GameplayTier | undefined;

export const nextGameplayTier = (gameplayTier: GameplayTier) =>
  tierRanksAway(gameplayTier, 1);

export const previousGameplayTier = (gameplayTier: GameplayTier) =>
  tierRanksAway(gameplayTier, -1);

/**
 * @returns the share, from 0 to 1, of the way from the tier's minimum L10 to the next tier's.
 */
export const nbaGameplayTierProgress = (
  gameplayTier: GameplayTier,
  l10Score: number
) => {
  const nextTier = nextGameplayTier(gameplayTier);
  if (!nextTier) {
    return 1;
  }

  const tierMin = NBA_GAMEPLAY_TIER_MIN_L10_SCORES[gameplayTier];
  const nextTierMin = NBA_GAMEPLAY_TIER_MIN_L10_SCORES[nextTier];

  return clamp((l10Score - tierMin) / (nextTierMin - tierMin), 0, 1);
};

const NBA_GAMEPLAY_TIERS_BY_STARS = [...GAMEPLAY_TIER_DISPLAY_ORDER].reverse();

export const NBA_MIN_STAR_RATING = gameplayTierStarValue(
  NBA_GAMEPLAY_TIERS_BY_STARS[0]
);
export const NBA_MAX_STAR_RATING = gameplayTierStarValue(
  NBA_GAMEPLAY_TIERS_BY_STARS[NBA_GAMEPLAY_TIERS_BY_STARS.length - 1]
);

export const nbaGameplayTierFromStars = (stars: number) =>
  NBA_GAMEPLAY_TIERS_BY_STARS.find(
    gameplayTier => gameplayTierStarValue(gameplayTier) === stars
  );

/**
 * @returns the L10 bounds covering the star ratings, undefined when unbounded.
 */
export const nbaStarRatingToL10Range = ({
  low,
  high,
}: {
  low: number;
  high: number;
}): [number | undefined, number | undefined] => {
  const lowTier = nbaGameplayTierFromStars(low);
  const highTier = nbaGameplayTierFromStars(high);
  const aboveHighTier = highTier && nextGameplayTier(highTier);

  return [
    lowTier && low > NBA_MIN_STAR_RATING
      ? NBA_GAMEPLAY_TIER_MIN_L10_SCORES[lowTier]
      : undefined,
    aboveHighTier ? NBA_GAMEPLAY_TIER_MIN_L10_SCORES[aboveHighTier] : undefined,
  ];
};

const nbaGameplayTierFromL10 = (l10Score: number) =>
  GAMEPLAY_TIER_DISPLAY_ORDER.find(
    gameplayTier => l10Score >= NBA_GAMEPLAY_TIER_MIN_L10_SCORES[gameplayTier]
  )!;

export const nbaL10RangeToStarRating = ([min, max]: [
  number | undefined,
  number | undefined,
]) => {
  const lowStars =
    min === undefined
      ? NBA_MIN_STAR_RATING
      : gameplayTierStarValue(nbaGameplayTierFromL10(min));

  if (max === undefined) {
    return { low: lowStars, high: NBA_MAX_STAR_RATING };
  }

  const highTier = nbaGameplayTierFromL10(max);
  // A max set from star ratings is the next tier's minimum, so it belongs to the
  // tier below. Any other max, e.g. a remaining cap, stays in its own tier.
  const isTierMinimum = max === NBA_GAMEPLAY_TIER_MIN_L10_SCORES[highTier];
  const ceilingTier =
    (isTierMinimum && previousGameplayTier(highTier)) || highTier;

  return {
    low: lowStars,
    high: gameplayTierStarValue(ceilingTier),
  };
};
